import React, { useState, lazy, Suspense, useEffect } from 'react';
import styled from 'styled-components';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { Film, Bed, X, ChevronLeft, ChevronRight, Maximize2, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import backgroundImage from '../images/10246984.jpeg';




const LazyReactPlayer = lazy(() => import('react-player'));
const LazyModel3DViewer = lazy(() => import('./Model3DViewer'));



const SERVER_URL = 'https://nuedesigns.com';



// ============ STYLED COMPONENTS ============



const ProjectPage = styled.div`
  min-height: 0;

  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;

  /* keep your current side padding */
  padding-inline: clamp(1rem, 1.4vw, 2rem);

  /* NEW: push everything down */
  padding-block-start: clamp(4.5rem, 2vh, 3rem);
  padding-block-end: clamp(1rem, 1.4vw, 2rem);

  overflow-x: hidden;

  @media (min-width: 1080px) and (min-height: 800px) {
    max-height: calc(100vh - 120px);
    overflow: hidden;
  }
`;

const Hero = styled.div`
  /* Less vertical waste on big screens, still fine on 1080 */
  height: clamp(12vh, 18vh, 20vh);
  position: relative;
  border-radius: 20px;
  overflow: hidden;

  margin-bottom: clamp(1.25rem, 2vw, 3rem);

  @media (max-width: 768px) {
    height: 40vh;
  }
`;



const HeroImage = styled(LazyLoadImage)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.6);
`;



const HeroContent = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 37.5%;
  transform: translateX(-50%);
  text-align: center;
  z-index: 2;
  width: 100%;
  padding: 0 1rem;

  @media (max-width: 1024px) {
    left: 50%;
  }

  @media (min-width: 1920px) {
    left: 41%;
  }

  @media (min-width: 2560px) {
    left: 43%;
  }

  @media (min-width: 3840px) {
    left: 45%;
  }
`;

const Title = styled.h1`
  font-family: "Raleway-Regular";
  color: #0a192f;
  font-size: clamp(2.5rem, 8vw, 2.75rem);
  margin: 0 0 1.75rem 0;
  font-weight: 700;
  letter-spacing: 0.03em;
`;



const Meta = styled.div`
  font-size: 1.1rem;
  color: #0a192f;
  opacity: 0.95;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;



  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;



const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  margin-top: -5.5rem;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;

  @media (min-width: 1920px) {
    margin-top: -3.5rem;
    gap: 2.5rem;
  }

  @media (min-width: 2560px) {
    margin-top: -2.75rem;
    gap: 2.25rem;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    margin-top: -1.5rem;
  }
`;



const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;



const Description = styled.p`
  font-family: "Raleway-Regular";
  font-size: 1.2rem;
  line-height: 1.7;
  color: #0a192f;
  margin: 0;
  margin-top: 2.5rem; 
  margin-bottom: -1.5rem;
  padding: 1.5rem;
  border-radius: 12px;



  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;



const Features = styled.div`

  padding: 2rem;
  border-radius: 16px;



  h3 {
  
    margin: 0 0 2.5rem 0;
    color: #0a192f;
    font-size: 1.3rem;
    font-family: "Raleway-Regular";
    font-weight: 800;
  }



  @media (max-width: 768px) {
    padding: 1.5rem;



    h3 {
      font-size: 1.1rem;
    }
  }
`;



const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
`;



const Feature = styled.li`
  background: #0a192fb9;
  padding: 1rem;
  border-radius: 10px;
  border-left: 4px solid #e1ad21;
  color: #cec9ab;
  font-size: 0.95rem;
  transition: all 0.3s ease;



  &:hover {
    background: #cec9ab;
    color: #0a192f;
    transform: translateX(4px);
  }
`;



const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: -4.75rem;

  @media (max-width: 1024px) {
    grid-column: 1 / -1;
    margin-top: 0;
  }
`;



const ActionButton = styled.button`
  background: linear-gradient(135deg, #0a192f, #112749);
  color: #cec9ab;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  width: 100%;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  box-shadow: 0 4px 15px #0a192f;
  will-change: transform;



  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 30px #0a192f;
  }



  &:active {
    transform: translateY(-2px);
  }



  @media (max-width: 768px) {
    padding: 0.875rem 1.5rem;
    font-size: 0.95rem;
  }
`;



const GallerySection = styled.div`
  background: rgba(10, 25, 47, 0.8);
  border-radius: 20px;
  padding: 1.5rem;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  min-height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  will-change: border-color;



  &:hover {
    border-color: #e1ad21;
    box-shadow: 0 0 30px #0a192f;
  }



  @media (max-width: 768px) {
    min-height: 280px;
    padding: 1rem;
  }
`;



const GalleryImage = styled(LazyLoadImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  border-radius: 16px;
`;



const GalleryCounter = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(10, 25, 47, 0.95);
  color: #cec9ab;
  padding: 0.65rem 1.2rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  z-index: 4;
`;



const GalleryNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(33, 128, 160, 0.0);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  opacity: 0;
  z-index: 5;
  color: #ffffff;
  font-weight: 700;



  ${GallerySection}:hover & {
    opacity: 1;
  }



  &:hover {
    background: #e1ad21;
    transform: translateY(-50%) scale(1.15);
    box-shadow: 0 8px 20px #0a192f;
  }



  &.prev {
    left: 1rem;
  }



  &.next {
    right: 1rem;
  }



  @media (max-width: 768px) {
    width: 36px;
    height: 36px;



    &.prev {
      left: 0.5rem;
    }



    &.next {
      right: 0.5rem;
    }
  }
`;



const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.98);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
  animation: fadeIn 0.3s ease;



  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;



const ModalContent = styled.div`
  position: relative;
  width: 90vw;
  height: 85vh;
  max-width: 1200px;
  background: rgba(10, 25, 47, 0.95);
  border-radius: 20px;
  padding: 1.5rem;
  border: 2px solid #e1ad21;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;



  @keyframes slideUp {
    from {
      transform: translateY(30px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }



  @media (max-width: 768px) {
    width: 96vw;
    height: 92vh;
    padding: 1rem;
  }
`;



const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: rgba(33, 128, 160, 0.0);
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10001;
  color: #ffffff;
  font-weight: 700;



  &:hover {
    background: #e1ad21;
    transform: scale(1.12);
    box-shadow: 0 8px 25px #0a192f;
  }



  &:active {
    transform: scale(1.05);
  }



  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    top: 1rem;
    right: 1rem;
  }
`;



const VideoContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;



  iframe {
    border: none;
    border-radius: 12px;
  }
`;



const FullscreenGallery = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  overflow: hidden;



  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;



const ModalGalleryNav = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(33, 128, 160, 0.0);
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  z-index: 10002;
  color: #ffffff;
  font-weight: 700;



  &:hover {
    background: #e1ad21;
    transform: translateY(-50%) scale(1.15);
    box-shadow: 0 8px 25px #0a192f;
  }



  &:active {
    transform: translateY(-50%) scale(1.08);
  }



  &.prev {
    left: 1.5rem;
  }



  &.next {
    right: 1.5rem;
  }



  @media (max-width: 768px) {
    width: 44px;
    height: 44px;


    &.prev {
      left: 1rem;
    }


    &.next {
      right: 1rem;
    }
  }
`;



// ============ MAIN COMPONENT ============



function BachelorPad() {
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [show3DModel, setShow3DModel] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);



  // Gallery images array
  const galleryImages = [
    `${SERVER_URL}/assets/images/Apartment1Bed.jpg`,
    `${SERVER_URL}/assets/images/BachelorPad1.jpg`,
    `${SERVER_URL}/assets/images/BachelorPad3.jpg`,
    `${SERVER_URL}/assets/images/BachelorPad2.jpg`,
    `${SERVER_URL}/assets/images/BachelorPad6.jpg`,
    `${SERVER_URL}/assets/images/BachelorPad4.jpg`,
    `${SERVER_URL}/assets/images/BachelorPad5.jpg`,
  ];



  // 3D Model path - use public folder for dev, SERVER_URL for production
  const modelPath = process.env.NODE_ENV === 'production' 
    ? `${SERVER_URL}/assets/models/Apartment1Bed.glb`
    : `/assets/models/Apartment1Bed.glb`;



  const handlePrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === 0 ? galleryImages.length - 1 : prev - 1
    );
  };



  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === galleryImages.length - 1 ? 0 : prev + 1
    );
  };



  const handleKeyDown = (e) => {
    if (!showGallery) return;
    if (e.key === 'ArrowLeft') {
      handlePrevImage(e);
    } else if (e.key === 'ArrowRight') {
      handleNextImage(e);
    }
  };



  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showGallery]);



  return (
    <ProjectPage>
      {/* HERO SECTION */}
      <Hero>
        <HeroContent>
          <Title>Bachelor Pad</Title>
          <Meta>
            Residential • 1 Bed <Bed size={24} />
          </Meta>
        </HeroContent>
      </Hero>



      {/* MAIN CONTENT */}
      <Content>
        {/* LEFT COLUMN: TEXT & FEATURES */}
        <TextSection>
          <Description>
            An exquisite apartment design that combines modern aesthetics with
            luxurious amenities for the ultimate living experience. Perfect for
            the discerning single professional seeking sophistication and
            comfort. This stunning 1-bedroom residence features premium finishes,
            intelligent space planning, and curated design elements that create
            an inviting sanctuary.
          </Description>



          <Features>
            <h3>Key Features</h3>
            <FeatureList>
              <Feature>✨ Modern Finish</Feature>
              <Feature>✨ Open-plan layout</Feature>
              <Feature>✨ Designer Kitchen</Feature>
              <Feature>✨ Smart Home Ready</Feature>
              <Feature>✨ Luxury Bathroom</Feature>
              <Feature>✨ Premium Materials</Feature>
            </FeatureList>
          </Features>
        </TextSection>



        {/* RIGHT COLUMN: ACTIONS & GALLERY */}
        <Sidebar>
          <GallerySection onClick={() => setShowGallery(true)}>
            <LazyLoadImage
              src={galleryImages[currentImageIndex]}
              alt="Apartment render"
              effect="blur"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <GalleryNav className="prev" onClick={handlePrevImage}>
              <ChevronLeft size={20} />
            </GalleryNav>
            <GalleryNav className="next" onClick={handleNextImage}>
              <ChevronRight size={20} />
            </GalleryNav>
            <GalleryCounter>
              {currentImageIndex + 1} / {galleryImages.length}
            </GalleryCounter>
          </GallerySection>

         {/* <ActionButton onClick={() => setShow3DModel(true)}>
            <Maximize2 size={20} />
            View 3D Model
          </ActionButton> */}



          <ActionButton onClick={() => setShowVideo(true)}>
            <Film size={20} />
            Watch Render Video
          </ActionButton>
        </Sidebar>
      </Content>



      {/* 3D MODEL MODAL */}
      {show3DModel && (
        <ModalOverlay onClick={() => setShow3DModel(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShow3DModel(false)}>
              <X size={24} />
            </CloseButton>
            <Suspense fallback={<div style={{ color: '#fff', textAlign: 'center' }}>Loading 3D model...</div>}>
              <LazyModel3DViewer modelPath={modelPath} />
            </Suspense>
          </ModalContent>
        </ModalOverlay>
      )}



      {/* VIDEO MODAL */}
      {showVideo && (
        <ModalOverlay onClick={() => setShowVideo(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowVideo(false)}>
              <X size={24} />
            </CloseButton>
            <VideoContainer>
              <Suspense fallback={<div style={{ color: '#fff' }}>Loading video...</div>}>
                <LazyReactPlayer
                  url="https://www.youtube.com/watch?v=w6gyRxdFV-c"
                  playing
                  controls
                  width="100%"
                  height="100%"
                  style={{ borderRadius: '12px' }}
                />
              </Suspense>
            </VideoContainer>
          </ModalContent>
        </ModalOverlay>
      )}



      {/* GALLERY MODAL */}
      {showGallery && (
        <ModalOverlay onClick={() => setShowGallery(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowGallery(false)}>
              <X size={24} />
            </CloseButton>
            <FullscreenGallery>
              <LazyLoadImage
                src={galleryImages[currentImageIndex]}
                alt="Apartment render fullscreen"
                effect="blur"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </FullscreenGallery>
            <ModalGalleryNav
              className="prev"
              onClick={handlePrevImage}
            >
              <ChevronLeft size={24} />
            </ModalGalleryNav>
            <ModalGalleryNav
              className="next"
              onClick={handleNextImage}
            >
              <ChevronRight size={24} />
            </ModalGalleryNav>
            <GalleryCounter style={{ bottom: '2rem' }}>
              {currentImageIndex + 1} / {galleryImages.length}
            </GalleryCounter>
          </ModalContent>
        </ModalOverlay>
      )}
    </ProjectPage>
  );
}



export default BachelorPad;
